import { slugify } from '../lib/helpers/common'
import { useLocalStorage } from '../lib/helpers/storage'

/**
 * Builds a VueMeta compatible script entry for GTM, depending on environment.  Of note,
 * non-production builds should default to the "dev" setup.
 *
 * @param {'prod'|'dev'} env The environment the script is for
 * @param {string} id The GTM property ID
 */
export const scriptTagsGTM = (env, id) => {
  let innerHTML

  switch (env) {
    case 'prod':
      innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`
      break
    default:
      innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl+ '&gtm_auth=-k7zXGFtpz-dMVJESBaNNA&gtm_preview=env-31&gtm_cookies_win=x';f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`
  }

  return { hid: 'gtm', innerHTML }
}

/**
 * Builds a VueMeta compatible noscript entry for GTM, depending on environment.  Of note,
 * non-production builds should default to the "dev" setup.
 *
 * @param {'prod'|'dev'} env The environment the script is for
 * @param {string} id The GTM property ID
 */
export const noScriptTagsGTM = (env, id) => {
  let innerHTML

  switch (env) {
    case 'prod':
      innerHTML = `<iframe title="gtm" src="https://www.googletagmanager.com/ns.html?id=${id}"height="0" width="0" style="display:none;visibility:hidden"></iframe>`
      break
    default:
      innerHTML = `<iframe title="gtm" src="https://www.googletagmanager.com/ns.html?id=${id}&gtm_auth=-k7zXGFtpz-dMVJESBaNNA&gtm_preview=env-31&gtm_cookies_win=x"height="0" width="0" style="display:none;visibility:hidden"></iframe>`
  }

  return { hid: 'gtm-ns', innerHTML, body: true }
}

/**
 * Convert a Logo into a GA4 product item.
 *
 * @param {Object} logo A Logo Object obtained from the API
 * @param {String} list The List the product belongs to
 */
export const convertLogoToProduct = (logo, list = 'Ideas') => {
  return {
    item_list_id: slugify(list),
    item_list_name: list,
    item_name: 'logo',
    quantity: 1,
    item_id: logo?.id,
    wordmark: logo?.wordmark,
    slogan: logo?.slogan,
    item_variant: logo?.assets?.template?.orientation,
    item_category: logo?.template,
    item_category2: logo?.assets?.palette?.id,
    item_category3: logo?.assets?.wordmark_fonts?.[0]?.id,
    item_category4: logo?.assets?.slogan_fonts?.[0]?.id,
    item_category5: logo?.assets?.icons?.[0]?.id,
    item_brand: useLocalStorage().getItem('eid'),
  }
}

/**
 * Convert a Plan into a GA4 Product.
 *
 * @param {Object} plan An account plan
 * @param {String} list The list this plan belongs to
 */
export const convertPlanToProduct = (plan, list = 'Ideas') => {
  return {
    item_brand: useLocalStorage().getItem('eid'),
    item_list_id: slugify(list),
    item_name: plan?.code,
    item_list_name: list,
    item_id: plan?.code,
    quantity: 1,
    item_category: undefined,
    item_category2: undefined,
    item_category3: undefined,
    item_category4: undefined,
    item_category5: undefined,
    item_variant: undefined,
  }
}

/**
 * Convert a Plan into a GA4 Product.
 *
 * @param {Object} plan An account plan
 * @param {String} list The list this plan belongs to
 */
export const convertCartItemToProduct = (item, list = 'Ideas') => {
  return {
    item_brand: useLocalStorage().getItem('eid'),
    item_list_id: slugify(list),
    item_name: item.package.name,
    item_list_name: list,
    item_id: item.package.code,
    quantity: 1,
    item_category: item.billing_interval || 'once',
    item_category2: undefined,
    item_category3: undefined,
    item_category4: undefined,
    item_category5: undefined,
    item_variant: undefined,
  }
}

/**
 * Convert a Logo into a GA4 cart item.
 *
 * @param {Object} item A cart item
 */
export const converCartItemToGaCart = item => {
  return {
    ...convertCartItemToProduct(item),

    item_name: item.package.code,
    item_coupon: item.credit,
    quantity: item.quantity,
    price: item.price / 100,
  }
}

/**
 * Convert our cart into GA4 cart items.
 *
 * @param {Object} cart
 */
export const convertCartToItems = ({ items }) => {
  return items.map(item => converCartItemToGaCart(item))
}
